import { useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { omit } from 'lodash';
import { useDispatch } from 'react-redux';
import { setReviewerViewSettings, useReviewersSettings } from 'reducers/reviewCreationSlice';

import { PEER_NOMINATION_COHORTS } from 'constants/ProjectConstants';

import { Accordion, AccordionButton } from 'uiToolkitV1/Accordion';
import { AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { Divider } from 'uiToolkitV1/Divider';
import { NumberInput } from 'uiToolkitV1/InputV2/NumberInput';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { AlertBar } from '../../components/AlertBar';

const MAX_ALLOWED_NOMINATION_COUNT = 100;

export const REVIEW_SETTINGS_PEER_POOL = [
  {
    value: PEER_NOMINATION_COHORTS.SENIORS,
    label: 'Leaders',
  },
  {
    value: PEER_NOMINATION_COHORTS.JUNIORS,
    label: 'Direct Team',
  },
  {
    value: PEER_NOMINATION_COHORTS.OUTSIDE_TEAM,
    label: 'Internal Clients',
  },
  {
    value: PEER_NOMINATION_COHORTS.PRIMARY_TEAM,
    label: 'Team Peers',
  },
];

const validatePeerPoolValues = (peerPools) => {
  let isValid = true;
  Object.keys(peerPools).forEach((poolType) => {
    const { minPeers, maxPeers } = peerPools[poolType];
    if (
      typeof minPeers !== 'number' ||
      typeof maxPeers !== 'number' ||
      Number.isNaN(minPeers) ||
      Number.isNaN(maxPeers) ||
      minPeers > maxPeers
    ) {
      isValid = false;
    }
  });
  return isValid;
};

const validateTotalMinMaxValues = ({ minPeers, maxPeers }) => {
  if (
    typeof minPeers !== 'number' ||
    typeof maxPeers !== 'number' ||
    Number.isNaN(minPeers) ||
    Number.isNaN(maxPeers) ||
    minPeers > maxPeers
  ) {
    return false;
  }
  return true;
};

const CountItem = ({ poolType, label, isSelected, onChangeSelection, minValue, maxValue, onChangeHandler }) => {
  const [renderCountKey, setRenderCountKey] = useState(0);

  // to reset the rendered state and number input are un-controlled
  const onChangeSelectionHandlerWithRerender = () => {
    onChangeSelection(poolType);
    setRenderCountKey((prev) => prev + 2);
  };

  return (
    <Flex alignItems="center" p={1}>
      <Checkbox isChecked={isSelected} onChange={onChangeSelectionHandlerWithRerender} w="200px">
        {label}
      </Checkbox>

      <TextLabel mr={2}>Min</TextLabel>
      <NumberInput
        value={minValue}
        onChange={(valueAsString, valueAsNumber) => onChangeHandler(valueAsNumber, 'minPeers', poolType)}
        textAlign="start"
        mr={6}
        w="115px"
        type="number"
        min={0}
        max={MAX_ALLOWED_NOMINATION_COUNT}
        inputMode="numeric"
        disabled={!isSelected}
        key={renderCountKey}
        format={(val) => (Number.isNaN(val) ? '' : val)}
        isInvalid={isSelected && !(minValue <= maxValue)}
      />

      <TextLabel mr={2} ml="24px">
        Max
      </TextLabel>

      <NumberInput
        value={maxValue}
        onChange={(valueAsString, valueAsNumber) => onChangeHandler(valueAsNumber, 'maxPeers', poolType)}
        textAlign="start"
        mr={6}
        w="115px"
        type="number"
        min={0}
        max={MAX_ALLOWED_NOMINATION_COUNT}
        inputMode="numeric"
        disabled={!isSelected}
        key={renderCountKey + 1}
        format={(val) => (Number.isNaN(val) ? '' : val)}
        isInvalid={isSelected && !(minValue <= maxValue)}
      />
    </Flex>
  );
};

const NominationCountSettings = ({ feedbackFormId }) => {
  const dispatch = useDispatch();
  let { minPeers, maxPeers, peerPools } = useReviewersSettings({
    feedbackFormId,
  });
  peerPools = peerPools ?? {};

  const isValidPeerPoolData = validatePeerPoolValues(peerPools);
  const isValidTotalMinMax = validateTotalMinMaxValues({ minPeers, maxPeers });

  const onChangeHandler = (newVal, propertyName) => {
    dispatch(
      setReviewerViewSettings({
        feedbackFormId,
        settings: { [propertyName]: newVal },
      })
    );
  };

  const items = REVIEW_SETTINGS_PEER_POOL.map((item) => ({
    label: item.label,
    poolType: item.value,
    isSelected: !!peerPools[item.value],
    minValue: peerPools[item.value]?.minPeers ?? NaN,
    maxValue: peerPools[item.value]?.maxPeers ?? NaN,
  }));

  const onChangeSelectionHandler = (poolType) => {
    const newPeerPools = peerPools[poolType]
      ? omit(peerPools, poolType)
      : { ...peerPools, [poolType]: { minPeers: 0, maxPeers: 1 } };
    dispatch(
      setReviewerViewSettings({
        feedbackFormId,
        settings: {
          peerPools: newPeerPools,
        },
      })
    );
  };

  const onChangeMinMaxHandler = (value, valueType, poolType) => {
    const newPeerPools = {
      ...peerPools,
      [poolType]: { ...peerPools[poolType], [valueType]: value },
    };
    dispatch(
      setReviewerViewSettings({
        feedbackFormId,
        settings: {
          peerPools: newPeerPools,
        },
      })
    );
  };

  return (
    <>
      <Box p="10px 8px" mt={3}>
        {!isValidTotalMinMax && <AlertBar text="Please enter valid min and max peer nomination count" mb={2} mt={-1} />}

        <TextBodyMedium mr={2} mb="2px">
          Set the range for peer nominations (minimum to maximum):
        </TextBodyMedium>
        <Flex alignItems="center" justifyContent="flex-start">
          <TextLabel mr={2}>Min</TextLabel>
          <NumberInput
            value={minPeers}
            onChange={(valueAsString, valueAsNumber) => onChangeHandler(valueAsNumber, 'minPeers')}
            textAlign="start"
            mr={6}
            w="115px"
            type="number"
            min={0}
            max={MAX_ALLOWED_NOMINATION_COUNT}
            inputMode="numeric"
            format={(val) => (Number.isNaN(val) ? '' : val)}
            isInvalid={!(minPeers <= maxPeers)}
          />
          <TextLabel mr={2}>Max</TextLabel>
          <NumberInput
            value={maxPeers}
            onChange={(valueAsString, valueAsNumber) => onChangeHandler(valueAsNumber, 'maxPeers')}
            textAlign="start"
            w="115px"
            type="number"
            min={0}
            max={MAX_ALLOWED_NOMINATION_COUNT}
            step={1}
            inputMode="numeric"
            format={(val) => (Number.isNaN(val) ? '' : val)}
            isInvalid={!(minPeers <= maxPeers)}
          />
        </Flex>
      </Box>

      {!isValidPeerPoolData && (
        <AlertBar text="Please enter valid min and max peer count for all sub categories" mt={3} />
      )}
      <Accordion
        allowMultiple
        allowToggle
        boxShadow="none !important"
        defaultIndex={Object.keys(peerPools).length > 0 ? [0] : []}
      >
        <AccordionItem2 sxProps={{ borderBottom: 'none !important' }}>
          <AccordionButton>
            <Flex alignItems="center">
              <TextBodyMedium mr={2}>Advanced Settings</TextBodyMedium>
              <TooltipInfoV1 title="You can set peer type specific min and max ranges" />
            </Flex>
          </AccordionButton>
          <AccordionPanel2>
            <Box
              border="1px solid"
              borderColor="neutral.EBEFF3"
              borderRadius="8px"
              bg="neutral.FDFDFD"
              ml="26px"
              p={3}
              pl={6}
            >
              {items.map((item, idx) => (
                <>
                  <CountItem
                    {...item}
                    onChangeHandler={onChangeMinMaxHandler}
                    onChangeSelection={onChangeSelectionHandler}
                  />
                  {idx !== items.length - 1 && <Divider my={3} />}
                </>
              ))}
            </Box>
          </AccordionPanel2>
        </AccordionItem2>
      </Accordion>
    </>
  );
};

export { NominationCountSettings };
